import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { storesAPI, ordersAPI, settingsAPI, utilsAPI } from '../lib/api';
import { BottomNav } from './MarketplaceHome';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ShoppingCart,
  Trash2,
  Minus,
  Plus,
  Store,
  MessageCircle,
  Package,
  CheckCircle2,
  Loader2,
  Copy,
  QrCode,
  FileText,
  User,
  MapPin,
  CreditCard,
  Search as SearchIcon,
} from 'lucide-react';

const INITIAL_FORM = {
  customer_name: '',
  customer_cpf: '',
  customer_phone: '',
  customer_email: '',
  customer_cep: '',
  customer_street: '',
  customer_number: '',
  customer_complement: '',
  customer_neighborhood: '',
  customer_city: '',
  customer_state: '',
  customer_reference: '',
  notes: '',
  payment_method: 'PIX',
};

const PAYMENT_METHODS = [
  { value: 'PIX', label: 'PIX' },
  { value: 'Cartão de Crédito', label: 'Cartão de Crédito' },
  { value: 'Cartão de Débito', label: 'Cartão de Débito' },
  { value: 'Dinheiro', label: 'Dinheiro' },
  { value: 'Outro', label: 'Outra forma' },
];

export const Cart = () => {
  const navigate = useNavigate();
  const { items, storeId, removeItem, updateQuantity, clearCart, total, itemCount } = useCart();
  const { user } = useAuth();
  const [store, setStore] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [footerCfg, setFooterCfg] = useState(null);
  const [formData, setFormData] = useState({
    ...INITIAL_FORM,
    customer_name: user?.name || '',
    customer_email: user?.email || '',
  });

  useEffect(() => {
    settingsAPI.getFooter().then(({ data }) => setFooterCfg(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (storeId) {
      loadStore();
    }
  }, [storeId]);

  const loadStore = async () => {
    try {
      const { data } = await storesAPI.get(storeId);
      setStore(data);
    } catch (error) {
      console.error('Error loading store:', error);
    }
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);

  const handleCepLookup = async () => {
    const digits = (formData.customer_cep || '').replace(/\D/g, '');
    if (digits.length !== 8) {
      toast.error('CEP inválido — digite 8 números.');
      return;
    }
    setCepLoading(true);
    try {
      const { data } = await utilsAPI.lookupCEP(digits);
      setFormData((f) => ({
        ...f,
        customer_street: data.street || f.customer_street,
        customer_neighborhood: data.neighborhood || f.customer_neighborhood,
        customer_city: data.city || f.customer_city,
        customer_state: data.state || f.customer_state,
      }));
      toast.success('Endereço preenchido pelo CEP.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Não conseguimos localizar esse CEP.');
    } finally {
      setCepLoading(false);
    }
  };

  const composedAddress = () => {
    const parts = [
      formData.customer_street && `${formData.customer_street}, ${formData.customer_number || 's/n'}`,
      formData.customer_complement,
      formData.customer_neighborhood,
      formData.customer_city && `${formData.customer_city} - ${formData.customer_state}`,
      formData.customer_cep,
    ].filter(Boolean);
    return parts.join(', ');
  };

  const generateWhatsAppMessage = (order) => {
    const o = order || {};
    const dt = new Date(o.created_at || Date.now());
    const date = dt.toLocaleDateString('pt-BR');
    const time = dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    let m = `🛒 *NOVO PEDIDO — DESTACK Venda Mais*\n\n`;
    m += `*Pedido:* #${o.protocol || '---'}\n`;
    m += `*Data:* ${date} — *Hora:* ${time}\n\n`;
    m += `*Loja:* ${o.store_name || store?.name || ''}\n\n`;
    m += `*Cliente:* ${o.customer_name || formData.customer_name}\n`;
    if (o.customer_cpf || formData.customer_cpf) m += `*CPF:* ${o.customer_cpf || formData.customer_cpf}\n`;
    m += `*WhatsApp:* ${o.customer_phone || formData.customer_phone}\n`;
    if (o.customer_email || formData.customer_email) m += `*E-mail:* ${o.customer_email || formData.customer_email}\n`;
    m += `*Endereço:* ${o.customer_address || composedAddress()}\n`;
    if (o.customer_reference || formData.customer_reference) {
      m += `*Ponto de referência:* ${o.customer_reference || formData.customer_reference}\n`;
    }
    m += `\n*Produtos:*\n`;
    (o.items || items).forEach((item) => {
      m += `- ${item.name} (Qtd: ${item.quantity}) - ${formatPrice(item.price * item.quantity)}\n`;
      if (item.selected_variations && item.selected_variations.length) {
        m += `  Opções: ${item.selected_variations.map((v) => `${v.group_name}: ${v.option_label}`).join(' | ')}\n`;
      }
    });
    m += `\n*Valor Total:* ${formatPrice(o.total || total)}\n`;
    m += `*Forma de pagamento:* ${o.payment_method || formData.payment_method}\n`;
    m += `*Status:* Aguardando confirmação\n`;
    if (o.notes || formData.notes) m += `\n*Observações:* ${o.notes || formData.notes}\n`;
    if (o.store_pix_key || store?.pix_key) {
      m += `\n*Chave PIX da Loja:* ${o.store_pix_key || store?.pix_key}`;
    }
    m += `\n\n_Pedido registrado no marketplace DESTACK Venda Mais_`;
    return encodeURIComponent(m);
  };

  const handleCheckout = async () => {
    const required = ['customer_name', 'customer_phone', 'customer_cep', 'customer_street', 'customer_number', 'customer_neighborhood', 'customer_city', 'customer_state'];
    const missing = required.filter((k) => !formData[k]);
    if (missing.length) {
      toast.error('Preencha todos os campos obrigatórios do endereço.');
      return;
    }
    setLoading(true);
    try {
      const orderData = {
        store_id: storeId,
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        customer_email: formData.customer_email,
        customer_cpf: formData.customer_cpf,
        customer_cep: formData.customer_cep,
        customer_street: formData.customer_street,
        customer_number: formData.customer_number,
        customer_complement: formData.customer_complement,
        customer_neighborhood: formData.customer_neighborhood,
        customer_city: formData.customer_city,
        customer_state: formData.customer_state,
        customer_reference: formData.customer_reference,
        customer_address: composedAddress(),
        items: items.map((item) => ({
          product_id: item.product_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          selected_variations: item.selected_variations || [],
        })),
        total: total,
        notes: formData.notes,
        payment_method: formData.payment_method,
      };
      const { data } = await ordersAPI.create(orderData);
      // Clear cart deterministically after successful order
      clearCart();
      setCreatedOrder(data);
      setOrderComplete(true);
      toast.success(`Pedido ${data.protocol} criado!`);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Erro ao processar pedido. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendToDestack = () => {
    const central = (footerCfg?.whatsapp_central || '').replace(/\D/g, '');
    if (!central) {
      toast.error('WhatsApp central da DESTACK não configurado. Contate o suporte.');
      return;
    }
    const msg = generateWhatsAppMessage(createdOrder);
    window.open(`https://wa.me/55${central}?text=${msg}`, '_blank');
  };

  const copyPix = () => {
    const key = createdOrder?.store_pix_key || store?.pix_key;
    if (!key) return;
    navigator.clipboard.writeText(key);
    toast.success('Chave PIX copiada!');
  };

  if (orderComplete && createdOrder) {
    const pixKey = createdOrder.store_pix_key || store?.pix_key;
    const pixHolder = createdOrder.store_pix_holder || store?.pix_holder || store?.name;
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-8 flex items-start justify-center" data-testid="order-complete">
        <div className="w-full max-w-md space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center shadow-sm">
            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-900">Pedido criado!</h1>
            <p className="text-sm text-slate-500 mt-1">Anote o seu protocolo para acompanhamento.</p>
            <div className="mt-4 px-4 py-3 rounded-xl bg-gradient-to-br from-blue-50 to-violet-50 border border-blue-200">
              <p className="text-[11px] uppercase tracking-widest text-blue-700 font-semibold">Protocolo</p>
              <p className="text-lg font-bold text-blue-900 font-mono mt-0.5" data-testid="order-protocol">
                {createdOrder.protocol}
              </p>
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-4">{formatPrice(createdOrder.total)}</p>
            <p className="text-xs text-slate-500 mt-1">Forma de pagamento: <strong className="text-slate-700">{createdOrder.payment_method}</strong></p>
          </div>

          {pixKey && createdOrder.payment_method === 'PIX' && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="pix-block">
              <div className="flex items-center gap-2 mb-3">
                <QrCode className="w-5 h-5 text-blue-600" />
                <h2 className="font-semibold text-slate-900">Pagamento via PIX</h2>
              </div>
              <p className="text-xs text-slate-500 mb-2">Pague diretamente para a loja usando a chave abaixo:</p>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 break-all text-sm font-mono text-slate-800">
                {pixKey}
              </div>
              {pixHolder && (
                <p className="text-xs text-slate-500 mt-2">Beneficiário: <span className="font-medium text-slate-700">{pixHolder}</span></p>
              )}
              <button
                onClick={copyPix}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm font-medium"
                data-testid="copy-pix-btn"
              >
                <Copy className="w-4 h-4" /> Copiar chave PIX
              </button>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="next-step-block">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <h2 className="font-semibold text-slate-900">Próximo passo</h2>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Envie os dados do seu pedido para a equipe DESTACK no WhatsApp. Eles validam o pagamento e encaminham para a loja responsável.
            </p>
            <Button
              onClick={handleSendToDestack}
              className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base"
              data-testid="send-destack-btn"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Enviar pedido para a DESTACK
            </Button>
            {!footerCfg?.whatsapp_central && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-3">
                ⚠️ O administrador ainda não configurou o WhatsApp central da DESTACK.
              </p>
            )}
          </div>

          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="w-full"
            data-testid="back-home-btn"
          >
            Voltar ao início
          </Button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20" data-testid="cart-empty">
        <header className="bg-white border-b sticky top-0 z-50">
          <div className="flex items-center px-4 py-3">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2">
              <ArrowLeft className="w-6 h-6 text-gray-700" />
            </button>
            <h1 className="font-semibold text-gray-900 ml-2">Carrinho</h1>
          </div>
        </header>

        <div className="flex flex-col items-center justify-center h-[60vh] px-6">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <ShoppingCart className="w-12 h-12 text-gray-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Carrinho vazio</h2>
          <p className="text-gray-500 text-center mb-6">
            Você ainda não adicionou nenhum produto ao carrinho
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Explorar Produtos
          </Button>
        </div>

        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-56" data-testid="cart-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2">
              <ArrowLeft className="w-6 h-6 text-gray-700" />
            </button>
            <h1 className="font-semibold text-gray-900 ml-2">Carrinho ({itemCount})</h1>
          </div>
          <button
            onClick={clearCart}
            className="text-red-500 text-sm font-medium"
            data-testid="clear-cart-btn"
          >
            Limpar
          </button>
        </div>
      </header>

      {!showCheckout ? (
        <>
          {/* Store Info */}
          {store && (
            <div className="bg-white px-4 py-3 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Store className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Comprando de</p>
                  <p className="font-semibold text-gray-900">{store.name}</p>
                </div>
              </div>
            </div>
          )}

          {/* Cart Items */}
          <div className="p-4 space-y-3">
            {items.map((item) => {
              const key = item.item_key || item.product_id;
              return (
              <div
                key={key}
                className="bg-white rounded-xl p-4 flex gap-4"
                data-testid={`cart-item-${key}`}
              >
                <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  {item.image ? (
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 truncate">{item.name}</h3>
                  {item.selected_variations && item.selected_variations.length > 0 && (
                    <p className="text-xs text-slate-500 mt-0.5" data-testid={`variations-${key}`}>
                      {item.selected_variations.map((v) => `${v.group_name}: ${v.option_label}`).join(' • ')}
                    </p>
                  )}
                  <p className="text-lg font-bold text-blue-600 mt-1">{formatPrice(item.price)}</p>

                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateQuantity(key, item.quantity - 1)}
                        className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center"
                        data-testid={`qty-minus-${key}`}
                      >
                        <Minus className="w-4 h-4 text-gray-600" />
                      </button>
                      <span className="font-semibold">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(key, item.quantity + 1)}
                        className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center"
                        data-testid={`qty-plus-${key}`}
                      >
                        <Plus className="w-4 h-4 text-white" />
                      </button>
                    </div>

                    <button
                      onClick={() => removeItem(key)}
                      className="p-2 text-red-500"
                      data-testid={`remove-${key}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="bg-white mx-4 rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 mb-4">Resumo do Pedido</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Subtotal ({itemCount} itens)</span>
                <span className="text-gray-900">{formatPrice(total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Entrega</span>
                <span className="text-green-600">A combinar</span>
              </div>
              <div className="border-t pt-2 mt-2 flex justify-between">
                <span className="font-semibold text-gray-900">Total</span>
                <span className="font-bold text-xl text-blue-600">{formatPrice(total)}</span>
              </div>
            </div>
          </div>
        </>
      ) : (
        /* Checkout Form — Cadastro Completo do Cliente */
        <div className="p-4 space-y-4">
          {/* Personal Data */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Dados Pessoais
            </h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Nome Completo *</Label>
                <Input
                  id="name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  placeholder="Seu nome completo"
                  data-testid="checkout-name"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cpf">CPF (opcional)</Label>
                  <Input
                    id="cpf"
                    value={formData.customer_cpf}
                    onChange={(e) => setFormData({ ...formData, customer_cpf: e.target.value })}
                    placeholder="000.000.000-00"
                    data-testid="checkout-cpf"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">WhatsApp *</Label>
                  <Input
                    id="phone"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                    placeholder="(00) 00000-0000"
                    data-testid="checkout-phone"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email">E-mail (opcional)</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  placeholder="voce@email.com"
                  data-testid="checkout-email"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Endereço de Entrega
            </h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="cep">CEP *</Label>
                <div className="flex gap-2">
                  <Input
                    id="cep"
                    value={formData.customer_cep}
                    onChange={(e) => setFormData({ ...formData, customer_cep: e.target.value })}
                    onBlur={() => { if ((formData.customer_cep || '').replace(/\D/g, '').length === 8) handleCepLookup(); }}
                    placeholder="00000-000"
                    data-testid="checkout-cep"
                    required
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCepLookup}
                    disabled={cepLoading}
                    className="whitespace-nowrap"
                    data-testid="checkout-cep-btn"
                  >
                    {cepLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SearchIcon className="w-4 h-4" />}
                    <span className="ml-1">Buscar</span>
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Cidade e estado são preenchidos automaticamente pelo CEP.</p>
              </div>
              <div className="grid grid-cols-[1fr_100px] gap-3">
                <div>
                  <Label htmlFor="street">Rua *</Label>
                  <Input
                    id="street"
                    value={formData.customer_street}
                    onChange={(e) => setFormData({ ...formData, customer_street: e.target.value })}
                    data-testid="checkout-street"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="number">Número *</Label>
                  <Input
                    id="number"
                    value={formData.customer_number}
                    onChange={(e) => setFormData({ ...formData, customer_number: e.target.value })}
                    data-testid="checkout-number"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="complement">Complemento</Label>
                <Input
                  id="complement"
                  value={formData.customer_complement}
                  onChange={(e) => setFormData({ ...formData, customer_complement: e.target.value })}
                  placeholder="Apto, bloco, referência..."
                  data-testid="checkout-complement"
                />
              </div>
              <div>
                <Label htmlFor="neighborhood">Bairro *</Label>
                <Input
                  id="neighborhood"
                  value={formData.customer_neighborhood}
                  onChange={(e) => setFormData({ ...formData, customer_neighborhood: e.target.value })}
                  data-testid="checkout-neighborhood"
                  required
                />
              </div>
              <div className="grid grid-cols-[1fr_100px] gap-3">
                <div>
                  <Label htmlFor="city">Cidade *</Label>
                  <Input
                    id="city"
                    value={formData.customer_city}
                    onChange={(e) => setFormData({ ...formData, customer_city: e.target.value })}
                    data-testid="checkout-city"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="state">UF *</Label>
                  <Input
                    id="state"
                    value={formData.customer_state}
                    onChange={(e) => setFormData({ ...formData, customer_state: e.target.value.toUpperCase().slice(0, 2) })}
                    data-testid="checkout-state"
                    maxLength={2}
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="reference">Ponto de referência</Label>
                <Input
                  id="reference"
                  value={formData.customer_reference}
                  onChange={(e) => setFormData({ ...formData, customer_reference: e.target.value })}
                  placeholder="Próximo à padaria, portão azul..."
                  data-testid="checkout-reference"
                />
              </div>
            </div>
          </div>

          {/* Payment method */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Forma de Pagamento
            </h3>
            <Select
              value={formData.payment_method}
              onValueChange={(v) => setFormData({ ...formData, payment_method: v })}
            >
              <SelectTrigger data-testid="checkout-payment-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <Label htmlFor="notes">Observações (opcional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Alguma instrução especial?"
              rows={2}
              data-testid="checkout-notes"
            />
          </div>

          {/* Order Summary */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-2">Resumo</h4>
            <div className="flex justify-between text-blue-800">
              <span>{itemCount} itens</span>
              <span className="font-bold">{formatPrice(total)}</span>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              O pagamento é combinado diretamente com a loja e o pedido é registrado com número único DESTACK.
            </p>
          </div>
        </div>
      )}

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-16 left-0 right-0 bg-white border-t p-4 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.12)]">
        {!showCheckout ? (
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold text-gray-900">{formatPrice(total)}</p>
            </div>
            <Button
              onClick={() => setShowCheckout(true)}
              className="flex-1 h-14 bg-green-600 hover:bg-green-700 text-lg font-semibold"
              data-testid="checkout-btn"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Finalizar Pedido
            </Button>
          </div>
        ) : (
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowCheckout(false)}
              className="flex-1 h-14"
            >
              Voltar
            </Button>
            <Button
              onClick={handleCheckout}
              disabled={loading}
              className="flex-1 h-14 bg-green-600 hover:bg-green-700 text-lg font-semibold"
              data-testid="confirm-order-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                  Confirmar Pedido
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default Cart;

